/**
 * PATH: packages/job-hub/src/jobs/qa/fillLibraryPhotoSrc.ts
 * FILE_ID: JOBHUB-QA-FILL-LIBRARY-PHOTO-SRC-001
 * PURPOSE: Put live photo addresses into empty frames of a saved ITP.
 * WHY: A library row stores the HTML from the moment it was saved. That
 *      HTML keeps its words. Photo frames that were empty get the address
 *      the checklist has now.
 */
import { previewPhotosCleared } from './itpPreviewRemoved'
import type { QaAreaExecution, QaEvidence } from './qaTypes'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
}

function photoAddress(row: QaEvidence): string {
  for (const value of [row.photo_url, row.display_url, row.asset_url]) {
    const text = value?.trim() ?? ''
    if (text) return text
  }
  return ''
}

function livePhotos(areas: QaAreaExecution[], number: string, title: string): string[] {
  for (const area of areas) {
    for (const checklist of area.checklists ?? []) {
      for (const item of checklist.items ?? []) {
        const itemNumber = String(item.display_number ?? item.item_number ?? '').trim()
        if (itemNumber !== number || item.title.trim() !== title) continue
        return (area.evidence ?? [])
          .filter((row) => row.item_instance === item.id && row.type === 'photo')
          .map(photoAddress)
          .filter(Boolean)
      }
    }
  }
  return []
}

function figureHtml(url: string): string {
  return (
    `<figure style="width:72px">` +
    `<div class="frame" style="width:72px;height:72px">` +
    `<img src="${escapeHtml(url)}" alt="" style="width:100%;height:100%;object-fit:contain;object-position:50% 50%;background:#f7f7f7" />` +
    `</div>` +
    `</figure>`
  )
}

function articleTitle(article: Element): { number: string; title: string } {
  const heading = article.querySelector('h3')
  const number = heading?.querySelector('.num')?.textContent?.trim() ?? ''
  const copy = heading?.cloneNode(true) as HTMLElement | undefined
  copy?.querySelector('.num')?.remove()
  return { number, title: copy?.textContent?.trim() ?? '' }
}

export function fillLibraryPhotoSrc(html: string, areas: QaAreaExecution[]): string {
  if (!html || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  for (const article of doc.querySelectorAll('article.check')) {
    // Reused from: packages/job-hub/src/jobs/qa/itpPreviewRemoved.ts (previewPhotosCleared)
    if (previewPhotosCleared(article)) continue
    const { number, title } = articleTitle(article)
    const urls = livePhotos(areas, number, title)
    if (urls.length === 0) continue
    const frames = [...article.querySelectorAll('.photos img')]
    const empty = frames.length === 0 || frames.every((img) => (img.getAttribute('src') ?? '').trim() === '')
    if (!empty) continue
    if (frames.length === 0) {
      const block = doc.createElement('div')
      block.className = 'photos'
      block.innerHTML = urls.map(figureHtml).join('')
      const notes = article.querySelector('.notes')
      if (notes) article.insertBefore(block, notes)
      else article.appendChild(block)
      continue
    }
    frames.forEach((img, index) => {
      const url = urls[index]
      if (url) img.setAttribute('src', url)
    })
  }
  if (/<html[\s>]/i.test(html)) return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`
  return doc.body.innerHTML
}
